import { useMemo, useState } from 'react';

import { CsMemoModalBase } from './CsMemoModalBase';
import { CsMemoAdapter, CsMemoRecord } from './csMemoAdapter';

export interface CsMemoButtonBaseProps<TTarget extends object> {
  adapter: CsMemoAdapter<TTarget>;
  target: TTarget;
  /** 메모장 제목 */
  title: string;
  subtitle?: string;
  /**
   * 이미 아는 메모. 주면 개수 배지·미리보기에 그대로 쓰고 모달도 재조회하지 않는다.
   * 없으면 캐시만 참조하고(개수 미상) 모달을 열 때 조회한다.
   */
  memos?: CsMemoRecord[];
  isLoading?: boolean;
  unavailable?: boolean;
  subLabelMap?: Map<string, string>;
  notice?: string;
  /** 'cell' = 표 안 칩, 'button' = 헤더용 버튼 */
  variant?: 'cell' | 'button';
  label?: string;
  /** 마지막 메모 본문을 칩 옆에 미리보기로 노출 */
  showPreview?: boolean;
  className?: string;
  /** 빈 타깃 대신 이 타깃으로 캐시를 구독 (행마다 쿼리 엔트리가 늘어나는 것 방지) */
  emptyTarget: TTarget;
}

/**
 * CS 메모 진입점 — 개수 배지를 보여주고 클릭하면 메모장을 연다.
 * 목록 어디에 붙여도 되도록 모달 상태를 스스로 들고 있다.
 */
export function CsMemoButtonBase<TTarget extends object>({
  adapter,
  target,
  title,
  subtitle,
  memos,
  isLoading,
  unavailable,
  subLabelMap,
  notice,
  variant = 'cell',
  label = 'CS메모',
  showPreview,
  className,
  emptyTarget,
}: CsMemoButtonBaseProps<TTarget>) {
  const [open, setOpen] = useState(false);
  // memos를 못 받은 경우에만 캐시를 구독한다 (조회는 하지 않음)
  const cached = adapter.useCache(memos === undefined ? target : emptyTarget);

  const list = memos ?? cached ?? undefined;
  const count = list?.length;
  const hasAdminOnly = !!list?.some((m) => m.adminOnly);

  const preview = useMemo(() => {
    if (!list || list.length === 0) return '';
    const last = list[list.length - 1];
    return last ? last.body.replace(/\s+/g, ' ').trim() : '';
  }, [list]);

  const tooltip = useMemo(() => {
    if (unavailable) return 'CS 메모 · 서버 미반영';
    if (!list) return 'CS 메모';
    if (list.length === 0) return 'CS 메모 · 없음';
    return list
      .slice(-5)
      .map((m) => `[${m.byName}${m.adminOnly ? '·관리자전용' : ''}] ${m.body}`)
      .join('\n');
  }, [list, unavailable]);

  const has = (count ?? 0) > 0;

  const cellClassName = has
    ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
    : 'border-transparent text-gray-300 hover:border-gray-200 hover:text-gray-500';

  const buttonClassName = has
    ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
    : 'border-gray-300 text-gray-600 hover:bg-gray-50';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={isLoading}
        title={tooltip}
        className={
          className ??
          (variant === 'button'
            ? `inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${buttonClassName}`
            : `inline-flex max-w-[180px] items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium disabled:opacity-50 ${cellClassName}`)
        }
      >
        <span aria-hidden>📝</span>
        {variant === 'button' ? <span>{label}</span> : null}
        {count !== undefined && count > 0 && (
          <span
            className={
              variant === 'button'
                ? 'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-indigo-600 px-1 text-[10px] font-bold text-white'
                : 'font-bold'
            }
          >
            {count}
          </span>
        )}
        {hasAdminOnly && (
          <span
            className="text-[10px] text-amber-600"
            title="관리자 전용 메모 포함"
            aria-label="관리자 전용 메모 포함"
          >
            ●
          </span>
        )}
        {showPreview && preview && (
          <span className="truncate text-[11px] font-normal text-gray-500">
            {preview}
          </span>
        )}
      </button>

      {open && (
        <CsMemoModalBase
          adapter={adapter}
          title={title}
          subtitle={subtitle}
          target={target}
          memos={memos}
          isLoading={isLoading}
          unavailable={unavailable}
          subLabelMap={subLabelMap}
          notice={notice}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
